import { useWebWorkerFn } from '@vueuse/core';
import { ContentType, deepMerge, message, RequestMethod } from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import { SERVICE_PREFIX } from '@mts/utils';
import { getRequestHeaders } from '@mts/utils/auth';
import { globSetting } from '@mts/utils/env';
import { router } from '@/router';
import { getGlobalStore } from '@/store';
import type { PageConfig } from '..';
import { type DictOption, getCFConfigDetail } from '.';

const fetchOrigin = window.origin + globSetting.apiUrl;

/** 系统字典缓存 */
export const DICT_FETCHED_MAP: Recordable<DictOption[]> = {};
export let DICT_CACHED_MAP: Recordable<DictOption[]> = {};

/** 获取全量系统字典 */
export const openDictMapWorker = async () => {
  const url = fetchOrigin + SERVICE_PREFIX.LC + '/dataTypeClassification/commonPullDownMap';
  const options = {
    method: RequestMethod.POST,
    headers: { 'Content-Type': ContentType.JSON, ...getRequestHeaders({ url }) },
    body: JSON.stringify({ useTypeList: ['1', '5'] }),
  };

  const { workerFn, workerTerminate } = useWebWorkerFn<
    (params: Recordable) => Promise<Recordable<DictOption[]>>
  >(
    async ({ url, options = {} }) => {
      const res = await fetch(url, options).then((res) => res.json());
      return res.code === 0 && res.data ? res.data : {};
    },
    { timeout: 6e5 },
  );

  try {
    DICT_CACHED_MAP = await workerFn({ url, options });
    Object.freeze(DICT_CACHED_MAP);
    console.debug('🛫 字典预抓取完成', DICT_CACHED_MAP);
  } finally {
    workerTerminate();
  }
};

/** 页面配置缓存 */
let CF_CACHED_MAP: Recordable<PageConfig> = {
  ...JSON.parse(localStorage.getItem('CF_CACHED_MAP') || '{}'),
};
/** 获取使用的页面配置 */
export const openCFConfigWorker = async () => {
  const usedCF = localStorage.getItem('mts-used-cf');
  if (!usedCF) return;

  const pageCodeList = getCachedCFList(usedCF);
  if (!pageCodeList.length) return;

  const url = fetchOrigin + SERVICE_PREFIX.LCP + '/configfactory/page/batchDetailByCode';
  const options = {
    method: RequestMethod.POST,
    headers: { 'Content-Type': ContentType.JSON, ...getRequestHeaders() },
    body: JSON.stringify({ pageCodeList: pageCodeList.map((i) => i.pageCode) }),
  };

  const { workerFn, workerTerminate } = useWebWorkerFn<
    (params: Recordable) => Promise<PageConfig[]>
  >(
    async ({ url, options = {} }) => {
      const res = await fetch(url, options).then((res) => res.json());
      const data = res.code === 0 && res.data ? res.data : [];
      cleanCFConfig(data);
      return data;
    },
    { timeout: 6e5, localDependencies: [cleanCFConfig] },
  );

  try {
    const res = await workerFn({ url, options });
    if (res.length) {
      CF_CACHED_MAP = {};
      res.forEach((item) => {
        if (item?.pageCode) {
          CF_CACHED_MAP[item.pageCode] = item;
        }
      });
      localStorage.setItem('CF_CACHED_MAP', JSON.stringify(CF_CACHED_MAP));
      console.debug('🛫 页面配置预抓取完成(CF_CACHED_MAP)', Object.keys(CF_CACHED_MAP));
    }
  } finally {
    workerTerminate();
  }
};

/** 清理页面配置 */
function cleanCFConfig(
  configs: PageConfig[],
  keysToRemove: string[] = [
    'deleted',
    'createBy',
    'createByDeptId',
    'createByFactoryId',
    'createTime',
    'updateBy',
    'updateTime',
    'tenantId',
    'parentId',
    'canUpdateRequired',
  ],
) {
  if (!configs.length) return;

  const keySet = new Set(keysToRemove);
  const stack: any[] = [...configs];
  const businessFields = new Set(['areaDTOList', 'fields', 'actions', 'buttons', 'linkPageAreas']);

  while (stack.length) {
    const current = stack.pop()!;
    if (!current) continue;

    if (Array.isArray(current)) {
      for (let i = 0; i < current.length; i++) {
        if (current[i] && typeof current[i] === 'object') {
          stack.push(current[i]);
        }
      }
      continue;
    }

    for (const k of Object.keys(current)) {
      if (keySet.has(k)) {
        delete current[k];
        continue;
      }

      const value = current[k];
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          if (businessFields.has(k)) {
            for (let i = 0; i < value.length; i++) {
              if (value[i] && typeof value[i] === 'object') {
                stack.push(value[i]);
              }
            }
          }
        } else {
          stack.push(value);
        }
      }
    }
  }
}

type CachedCF = Pick<PageConfig, 'pageCode' | 'version'>;
const getCachedCFList = (str: string, maxFetch = 20) => {
  const items = str
    .slice(1)
    .split('|')
    .slice(maxFetch * -1);
  const temps: CachedCF[] = [];
  const seen = new Set();

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    const atIndex = item.indexOf('@');
    const pageCode = atIndex === -1 ? item : item.substring(0, atIndex);

    if (!seen.has(pageCode)) {
      const version = atIndex === -1 ? 0 : +item.substring(atIndex + 1);
      temps.push({ pageCode, version });
      seen.add(pageCode);
    }
  }
  return temps;
};
setTimeout(openCFConfigWorker, 3e3);

export const getCFConfigWithCache = async (pageCode: string): Promise<void | PageConfig> => {
  const cachedConfig = CF_CACHED_MAP[pageCode];
  if (!cachedConfig) {
    const res = await getCFConfigDetail({ pageCode });
    if (res) {
      cacheUsedCF(res);
      CF_CACHED_MAP[pageCode] = res;
      getGlobalStore().appendRouteConfig(res);
    }
    return res;
  }

  getCFConfigDetail({ pageCode, version: cachedConfig.version }).then((res) => {
    cacheUsedCF(res || { pageCode, version: cachedConfig.version });
    if (res) {
      console.warn('🚑 PAGE CACHE UPDATE >>', res.pageCode);
      CF_CACHED_MAP[pageCode] = res;
      localStorage.setItem('CF_CACHED_MAP', JSON.stringify(CF_CACHED_MAP));
      message.info(t('g.26080005'));
      getGlobalStore().disableRouteTransition = true;
      router.replace({ query: deepMerge(router.currentRoute.value.query, { v: res.version }) });
    }
  });
  console.debug('🚀 PAGE FROM CACHE >>', pageCode);
  getGlobalStore().appendRouteConfig(cachedConfig);
  return cachedConfig;
};

export function getCachedCFVersion(pageCode: string) {
  const usedCF = localStorage.getItem('mts-used-cf');
  if (!usedCF) return;

  const lastPos = usedCF.lastIndexOf(pageCode);
  if (lastPos === -1) return;

  const atIndex = usedCF.indexOf('@', lastPos);
  if (atIndex === -1) return;

  const nextPipe = usedCF.indexOf('|', atIndex + 1);
  return nextPipe === -1 ? usedCF.slice(atIndex + 1) : usedCF.slice(atIndex + 1, nextPipe);
}

function getUsedCF(pageCode: string) {
  const usedCF = localStorage.getItem('mts-used-cf') || '';
  const target = `|${pageCode}@`;
  const start = usedCF.indexOf(target);
  if (start === -1) return usedCF;
  const end = usedCF.indexOf('|', start + target.length);
  return end === -1 ? usedCF.slice(0, start) : usedCF.slice(0, start) + usedCF.slice(end);
}
const cacheUsedCF = ({ pageCode, version = 1 }: CachedCF) =>
  localStorage.setItem('mts-used-cf', `${getUsedCF(pageCode)}|${pageCode}@${version}`);
